import { useEffect, useRef } from "react";

interface TrustPilotWidgetProps {
  templateId?: string;
  businessUnitId?: string;
  styleHeight?: string;
  styleWidth?: string;
  token?: string;
}

export default function TrustPilotWidget({
  templateId = "56278e9abfbbba0bdcd568bc",
  businessUnitId = "68bf0b2034cd8124d1d3000c",
  styleHeight = "52px",
  styleWidth = "100%",
  token = "4f2f7b2a-bb51-4a15-aa70-133c615190ff",
}: TrustPilotWidgetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
    const cleanups: Array<() => void> = [];

    // Only hand Trustpilot an element that still exists and is in the document — calling
    // loadFromElement(null) is what logged "No element supplied to Trustpilot.loadFromElement".
    const initWidget = () => {
      const el = ref.current;
      if (cancelled || !el || !el.isConnected || !window.Trustpilot) return;
      window.Trustpilot.loadFromElement(el, true);
    };

    // window.Trustpilot appears once the bootstrap script has executed. Poll briefly (also
    // covers a <script> another instance already added that is still loading); give up after 5s.
    const waitForTrustpilot = () => {
      if (window.Trustpilot) {
        initWidget();
        return;
      }
      pollTimer = setInterval(() => {
        if (cancelled || !window.Trustpilot) return;
        clearInterval(pollTimer);
        clearTimeout(timeoutTimer);
        initWidget();
      }, 100);
      timeoutTimer = setTimeout(() => {
        clearInterval(pollTimer);
        if (!cancelled && !window.Trustpilot) {
          console.warn('[TrustPilot] Timed out waiting for window.Trustpilot');
        }
      }, 5000);
    };

    const loadScript = () => {
      if (cancelled) return;
      if (!document.querySelector("#trustpilot-script")) {
        const script = document.createElement("script");
        script.id = "trustpilot-script";
        script.type = "text/javascript";
        script.src = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
        script.async = true;
        script.onerror = () => {
          console.error('[TrustPilot] Failed to load script');
        };
        document.head.appendChild(script);
      }
      waitForTrustpilot();
    };

    // Never start the third-party script during the page's initial load: wait for window
    // "load", then for an idle period (with a timeout so it still happens on busy pages).
    const whenIdleAfterLoad = (cb: () => void) => {
      const scheduleIdle = () => {
        if (cancelled) return;
        if (typeof window.requestIdleCallback === "function") {
          const handle = window.requestIdleCallback(cb, { timeout: 3000 });
          cleanups.push(() => window.cancelIdleCallback(handle));
        } else {
          const t = setTimeout(cb, 200);
          cleanups.push(() => clearTimeout(t));
        }
      };
      if (document.readyState === "complete") {
        scheduleIdle();
      } else {
        window.addEventListener("load", scheduleIdle, { once: true });
        cleanups.push(() => window.removeEventListener("load", scheduleIdle));
      }
    };

    // Also only once this instance is about to scroll into view — this component renders in
    // the Footer on every page, so most page loads never need it at all. Fall back to the
    // load+idle trigger alone if IntersectionObserver isn't available.
    const NEAR_VIEWPORT_PX = 200;
    const isStillNearViewport = () => {
      const el = ref.current;
      if (!el || !el.isConnected) return false;
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight + NEAR_VIEWPORT_PX && rect.bottom > -NEAR_VIEWPORT_PX;
    };

    if (typeof IntersectionObserver === 'undefined' || !ref.current) {
      whenIdleAfterLoad(loadScript);
    } else {
      let observer: IntersectionObserver | undefined;
      // The observer can fire while the page is still short (skeletons/loading state put
      // the footer in range) — on a product page the loading state is short enough that the
      // footer is in view for ~1.5 s, and window "load" + idle land inside that window. So
      // after load + idle, let the page settle (first scroll, or SETTLE_MS) and re-check: if
      // the real content has since pushed this element out of range, re-arm and wait for the
      // visitor to scroll near it.
      const SETTLE_MS = 5000;
      const recheckWhenSettled = () => {
        if (cancelled) return;
        let done = false;
        const check = () => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          window.removeEventListener('scroll', check);
          if (cancelled) return;
          if (isStillNearViewport()) loadScript();
          else arm();
        };
        const timer = setTimeout(check, SETTLE_MS);
        window.addEventListener('scroll', check, { passive: true });
        cleanups.push(() => {
          clearTimeout(timer);
          window.removeEventListener('scroll', check);
        });
      };
      const arm = () => {
        if (cancelled || !ref.current) return;
        observer = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) {
              observer?.disconnect();
              whenIdleAfterLoad(recheckWhenSettled);
            }
          },
          { rootMargin: `${NEAR_VIEWPORT_PX}px 0px` }
        );
        observer.observe(ref.current);
      };
      arm();
      cleanups.push(() => observer?.disconnect());
    }

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return (
    <div
      ref={ref}
      className="trustpilot-widget"
      data-locale="en-US"
      data-template-id={templateId}
      data-businessunit-id={businessUnitId}
      data-style-height={styleHeight}
      data-style-width={styleWidth}
      data-token={token}
    >
      <a
        href="https://www.trustpilot.com/review/veevoerwinkel.co.za"
        target="_blank"
        rel="noopener noreferrer"
      >
        Trustpilot
      </a>
    </div>
  );
}

// Type declaration for Trustpilot global
declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement | null, force?: boolean) => void;
    };
  }
}
